import { isSupportedPublicCvBrowserFile } from "./publicApplicationCv";

const EXTENSION_BY_MIME: Record<string, string> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

export interface EmployerCvUploadDescriptor {
  contentSha256: string;
  extension: string;
  mimeType: string;
}

export async function buildEmployerCvUploadDescriptor(file: File): Promise<EmployerCvUploadDescriptor> {
  if (!isSupportedPublicCvBrowserFile(file.name, file.type)) {
    throw new Error("Unsupported CV file type");
  }

  const extensionFromName = file.name.split(".").pop()?.toLowerCase() ?? "";
  const suppliedMime = file.type.trim().toLowerCase().split(";", 1)[0];
  const extension = suppliedMime && suppliedMime !== "application/octet-stream"
    ? EXTENSION_BY_MIME[suppliedMime]
    : extensionFromName;
  if (!extension) throw new Error("Unsupported CV file type");

  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  const contentSha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
  return { contentSha256, extension, mimeType: MIME_BY_EXTENSION[extension] };
}

export async function buildEmployerCvStoragePath(input: {
  orgId: string;
  jobId: string;
  applicationId?: string;
  file: File;
}): Promise<string> {
  const descriptor = await buildEmployerCvUploadDescriptor(input.file);
  const applicationSegment = input.applicationId ? `${input.applicationId}/` : "";
  return `${input.orgId}/${input.jobId}/${applicationSegment}${descriptor.contentSha256}.${descriptor.extension}`;
}

export async function buildApplicationCvVersionUpload(input: {
  orgId: string;
  jobId: string;
  applicationId: string;
  file: File;
}): Promise<{ storagePath: string; contentSha256: string; mimeType: string }> {
  const descriptor = await buildEmployerCvUploadDescriptor(input.file);
  const storagePath = `${input.orgId}/${input.jobId}/${input.applicationId}/${descriptor.contentSha256}.${descriptor.extension}`;
  return {
    storagePath,
    contentSha256: descriptor.contentSha256,
    mimeType: descriptor.mimeType,
  };
}

export function isExistingStorageObjectError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { statusCode?: string | number; message?: string; error?: string };
  const statusCode = Number(candidate.statusCode);
  const message = `${candidate.message ?? ""} ${candidate.error ?? ""}`.toLowerCase();
  return statusCode === 409 || message.includes("already exists") || message.includes("duplicate");
}
